import { useTranslationDetector } from "@/hooks/useTranslationDetector";
import { Link } from "@/i18n/navigation";
import { dateFormats } from "@/utils/dateFormatter";
import decodeHTML from "@/utils/decodeHTML";
import { useLocale } from "next-intl";
import Image from "next/image";

/**
 * CTA tıklamasında dataLayer'a push eder (cta_click).
 * dataLayerPayload section config'den gelir (PageBuilder); cta_button blog başlığıdır.
 */
function pushCtaClickDataLayer(dataLayerPayload, cta_button) {
  if (typeof window === "undefined" || !dataLayerPayload) return;
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({
    ...dataLayerPayload,
    cta_button: cta_button ?? "",
  });
}

/*
  example data:
  {
    "id": 24574,
    "date": "2025-06-27T12:55:35",
    "slug": "kasta-nerede-kalinir-en-guzel-kas-otelleri",
    "link": "https://www.turna.com/blog/kasta-nerede-kalinir-en-guzel-kas-otelleri",
    "title": {
      "rendered": "Kaş&#8217;ta Nerede Kalınır? En İyi Kaş Otelleri"
    },
    "content": {
      "rendered": "",
      "protected": false
    },
    "excerpt": {
      "rendered": "<p>Akdeniz’in en güzel kıyılarından biri olan Kaş, Türkiye’nin en sevilen tatil rotalarından biri. Masmavi koyları, taş evleriyle süslenmiş dar sokakları ve her köşesinden sürpriz çıkaran yapısıyla Kaş, deniz-kum-güneş üçlüsünden ibaret değil; bir keşif ve dinginlik yeri. Tatil anlayışınız ne olursa olsun, Kaş’ta sizi kendine hayran bırakacak bir şey mutlaka var. Bu yazımızda Kaş’ta nerede kalınır...</p>"
    },
    "author": 45,
    "categories": [
      1811
    ],
    "tags": [
      306,
      5340,
      5339,
      358,
      978
    ],
    "ImageUrl": "https://res.cloudinary.com/turna/image/upload/h_214,q_auto,f_auto,c_fill,e_art:primavera,e_contrast:40/v1655828234/ka%C5%9F.jpg",
    "Author": {
      "slug": "erendiz-ozkurt",
      "name": "Erendiz Özkurt",
      "nickname": "erendiz-ozkurt"
    },
    "Excerpt": "<p>Akdeniz’in en güzel kıyılarından biri olan Kaş, Türkiye’nin en sevilen tatil rotalarından biri. Masmavi koyları, taş evleriyle süslenmiş dar sokakları ve her köşesinden sürpriz çıkaran yapısıyla Kaş, deniz-kum-güneş üçlüsünden ibaret değil; bir keşif ve dinginlik yeri. Tatil anlayışınız ne olursa olsun, Kaş’ta sizi kendine hayran bırakacak bir şey mutlaka var. Bu yazımızda Kaş’ta nerede kalınır...</p>",
    "Title": "Kaş&#8217;ta Nerede Kalınır? En İyi Kaş Otelleri",
    "MobileExcerpt": "<p>Akdeniz’in en güzel kıyılarından biri olan Kaş, Türkiye’nin en sevi...</p>",
    "DateFormatted": "2025-06-27"
  },

*/
export default function BlogCard({
  post,
  translationNamespace = "common",
  className = "",
  href = "/",
  showAuthor = true,
  showDate = true,
  index = 0,
  dataLayerPayload,
}) {
  const t = useTranslationDetector(translationNamespace);
  const locale = useLocale();
  const {
    id,
    Title,
    ImageUrl = "/images/dummy/blog-placeholder.png",
    Excerpt = "",
    Author = {},
    DateFormatted = "",
    Date = "",
    Link: link = "",
    slug = Title?.toLowerCase().replace(/\s+/g, "-"),
  } = post;

  const date = DateFormatted || Date;

  // Format the date: 2025-10-07 -> 7 Eki 2025 (or localized format)
  const formattedDate = date ? dateFormats.standard(date, locale) : "";

  const titleText = Title ? decodeHTML(Title) : slug;
  const handleClick = () => {
    if (dataLayerPayload) {
      pushCtaClickDataLayer(dataLayerPayload, titleText);
    }
  };

  return (
    <Link
      href={link || href}
      title={titleText}
      className={`border-border-card flex flex-col items-start justify-start overflow-hidden rounded-lg border xl:h-[445px] ${className}`}
      target="_blank"
      onClick={handleClick}
    >
      <div className="relative aspect-[147/120] w-full xl:aspect-[5/3]">
        <Image
          src={ImageUrl}
          alt={Title ? decodeHTML(Title) : slug}
          fill
          className="rounded-t-lg object-cover"
          placeholder="blur"
          blurDataURL="data:image/jpeg;base64,/9j/4AAQSkZJRgABAQAAAQABAAD/2wBDAAYEBQYFBAYGBQYHBwYIChAKCgkJChQODwwQFxQYGBcUFhYaHSUfGhsjHBYWICwgIyYnKSopGR8tMC0oMCUoKSj/2wBDAQcHBwoIChMKChMoGhYaKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCj/wAARCAABAAEDASIAAhEBAxEB/8QAFQABAQAAAAAAAAAAAAAAAAAAAAv/xAAUEAEAAAAAAAAAAAAAAAAAAAAA/8QAFQEBAQAAAAAAAAAAAAAAAAAAAAX/xAAUEQEAAAAAAAAAAAAAAAAAAAAA/9oADAMBAAIRAxEAPwCdABmX/9k="
          sizes="(max-width: 768px) 75vw, (max-width: 1024px) 38vw, 32vw"
          onError={(e) => {
            e.target.src = `/images/dummy/blog-dummy-${(index % 4) + 1}.jpg`;
          }}
        />
      </div>

      <div className="flex h-full w-full flex-col items-start justify-between space-y-[21px] p-4">
        <div className="flex flex-col gap-2">
          {Title && (
            <h3 className="text-foreground m-0 line-clamp-2 text-lg font-semibold">
              {decodeHTML(Title)}
            </h3>
          )}
          {Excerpt && (
            <div
              className="text-foreground-muted line-clamp-2 text-sm font-normal"
              dangerouslySetInnerHTML={{ __html: Excerpt }}
            />
          )}
        </div>

        {(showAuthor || showDate) && (
          <div className="space-y-1">
            {showAuthor && Author && (
              <p className="text-foreground text-base font-semibold">
                {Author?.name || Author}
              </p>
            )}
            {showDate && formattedDate && (
              <p className="text-muted-subtext text-sm font-normal">
                {formattedDate}
              </p>
            )}
          </div>
        )}
      </div>
    </Link>
  );
}
